import { useSyncExternalStore } from 'react';

/**
 * An Opendoor employee viewing a customer's dashboard (`?customer=<uuid>`) must not change that
 * customer's data as a side effect of looking at it. An employee who genuinely needs to complete
 * a step for a customer turns write mode on from the banner; it lasts until they turn it off or
 * reload.
 *
 * Blocking is derived from the two flags rather than stored, so a real customer session can never
 * end up blocked and can never reach write mode.
 */

let impersonatedCustomerUUID: string | null = null;
let writeModeEnabled = false;
/**
 * The viewed customer's name, published by whatever has the lead loaded. The banner renders at the
 * app root, above the routes that hold lead data, so it cannot read this from context.
 */
let impersonatedCustomerName: string | null = null;

const listeners = new Set<() => void>();
const impersonatedCustomerListeners = new Set<(customerUUID: string | null) => void>();

const notify = (): void => {
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const subscribeToImpersonatedCustomer = (
  listener: (customerUUID: string | null) => void,
): (() => void) => {
  impersonatedCustomerListeners.add(listener);
  return () => {
    impersonatedCustomerListeners.delete(listener);
  };
};

const useStore = <T>(read: () => T): T => useSyncExternalStore(subscribe, read, read);

/**
 * Keyed by the customer being viewed, so switching customers counts as a change and drops write
 * mode rather than carrying an opt-in granted for one customer onto the next.
 */
export const setImpersonatedCustomer = (customerUUID: string | null): void => {
  if (impersonatedCustomerUUID === customerUUID) {
    return;
  }
  impersonatedCustomerUUID = customerUUID;
  writeModeEnabled = false;
  impersonatedCustomerName = null;
  impersonatedCustomerListeners.forEach((listener) => listener(customerUUID));
  notify();
};

export const setWriteModeEnabled = (value: boolean): void => {
  if (writeModeEnabled === value) {
    return;
  }
  writeModeEnabled = value;
  notify();
};

export const setImpersonatedCustomerName = (name: string | null): void => {
  if (impersonatedCustomerName === name) {
    return;
  }
  impersonatedCustomerName = name;
  notify();
};

/**
 * True whenever an employee is viewing a customer, in either mode. Automatic side effects that
 * record "the customer saw this" key off this rather than the write guard, because write mode
 * covers deliberate actions taken for a customer, not the employee's own page views.
 */
export const isImpersonatingSession = (): boolean => impersonatedCustomerUUID !== null;

export const isWriteModeEnabled = (): boolean => writeModeEnabled;

export const areMutationsBlocked = (): boolean => isImpersonatingSession() && !writeModeEnabled;

export const getImpersonatedCustomer = (): string | null => impersonatedCustomerUUID;

const getImpersonatedCustomerName = (): string | null => impersonatedCustomerName;

export const useMutationsBlocked = (): boolean => useStore(areMutationsBlocked);

export const useIsImpersonatingSession = (): boolean => useStore(isImpersonatingSession);

export const useImpersonatedCustomer = (): string | null => useStore(getImpersonatedCustomer);

export const useImpersonatedCustomerName = (): string | null =>
  useStore(getImpersonatedCustomerName);

/** Sentry and RUM filter on this, so a refused write never lands in the error count. */
export const READ_ONLY_BLOCK_PREFIX = 'Blocked while impersonating';

/**
 * The operation the link most recently refused, so the UI can say which action did not save
 * rather than leaving the employee to notice on their own. Carries a counter because the same
 * operation blocked twice in a row is still two separate refusals worth reporting.
 */
type BlockedMutation = { operationName: string; count: number };

let lastBlocked: BlockedMutation | null = null;

const recordBlockedMutation = (operationName: string): void => {
  lastBlocked = { operationName, count: (lastBlocked?.count ?? 0) + 1 };
  notify();
};

/**
 * Records the refusal and returns the message to fail with. The Apollo link and the fetch guard
 * disagree on what a write is and on which error to throw, but both owe the toast a record and the
 * message a prefix, so pairing them here means a caller cannot do one without the other.
 */
export const refuseWrite = (label: string): string => {
  recordBlockedMutation(label);
  return `${READ_ONLY_BLOCK_PREFIX}: ${label}`;
};

export const lastBlockedMutation = (): BlockedMutation | null => lastBlocked;

export const useLastBlockedMutation = (): BlockedMutation | null => useStore(lastBlockedMutation);
